import { useCallback } from 'react';
import { useLocation } from 'react-router';
import type { PAGE_POSITIONS, PageType } from 'types';
import type {
  ProductListItem,
  ProductListItemExternal,
  ProductListItemInlineExternal,
  ProductListItemInternal
} from 'types/capi/shared';

import ProductNotificationCount from '~/components/ProductNotificationCount';
import AffiliateLinkClickEvent from '~/events/AffiliateLinkClickEvent';
import InlineProductCardImpressionEvent from '~/events/InlineProductCardImpressionEvent';
import InlineProductCardInteractionEvent from '~/events/InlineProductCardInteractionEvent';
import ProductCardClickEvent from '~/events/ProductCardClickEvent';
import type { ProductCardDisplayType } from '~/events/ProductCardInteractionEvent';
import ProductCardInteractionEvent from '~/events/ProductCardInteractionEvent';
import useEnv from '~/hooks/useEnv';
import useImpression from '~/hooks/useImpression';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useProductCardImpression from '~/hooks/useProductCardImpression';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import wct from '~/services/wct.client';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

import Image from '../Image';
import ImageMetadata from '../ImageMetadata';
import ProductNotificationButton from '../ProductNotificationButton';
import StockStatus from '../StockStatus';
import Tracked from '../Tracked';
import ClickOutLink from './ClickOutLink';

type Props = {
  allowNotifications: boolean;
  cardSize: 'small' | 'large';
  className?: string;
  displayType?: ProductCardDisplayType;
  imageLoading?: 'lazy' | 'eager';
  isMetadataEndAligned?: boolean;
  listId?: string | null;
  modulePosition?: number;
  moduleType: string;
  name?: TrackedButtonClickName;
  overrideInteractionPagePosition?: boolean;
  padImage: boolean;
  pagePosition: PAGE_POSITIONS;
  showNotificationCount?: boolean | 'always';
  withMetadata?: boolean;
};

interface ExternalProps extends Props {
  openPdp?: boolean;
  pageType?: PageType;
  position: number;
  product: ProductListItemExternal;
}

interface InternalProps extends Props {
  onClick?: () => void;
  pageType: PageType;
  position: number;
  product: ProductListItemInternal;
}

const ExternalProductCard = ({
  allowNotifications,
  cardSize,
  name,
  className,
  imageLoading,
  isMetadataEndAligned,
  listId = null,
  displayType,
  modulePosition,
  moduleType,
  position,
  openPdp = true,
  overrideInteractionPagePosition = false,
  padImage,
  pagePosition,
  product,
  showNotificationCount = false,
  pageType,
  withMetadata
}: ExternalProps) => {
  const { isBot } = useRootData();
  const impressionRef = useProductCardImpression({
    displayType,
    isBot,
    modulePosition,
    moduleType,
    position,
    product
  });

  return (
    <div ref={impressionRef} className={`relative ${className}`}>
      <ClickOutLink
        affiliate={product.affiliate}
        buttonPosition={0}
        displayType={displayType}
        listId={listId}
        modulePosition={modulePosition}
        moduleType={moduleType}
        position={position}
        name={name}
        pagePosition={pagePosition}
        price={product.price}
        product={product}
        pageType={pageType}
        urlTemplates={product}
        variant="none"
        className="flex cursor-pointer flex-col justify-center gap-3.5 overflow-hidden"
        openPdp={openPdp}
      >
        <CardContents
          allowNotifications={allowNotifications}
          withMetadata={withMetadata}
          cardSize={cardSize}
          padImage={padImage}
          pagePosition={pagePosition}
          product={product}
          imageLoading={imageLoading}
          isMetadataEndAligned={isMetadataEndAligned}
          showNotificationCount={showNotificationCount}
        />
      </ClickOutLink>
      <CardInteraction
        product={product}
        allowNotifications={allowNotifications}
        modulePosition={modulePosition}
        moduleType={moduleType}
        position={position}
        pagePosition={
          overrideInteractionPagePosition ? pagePosition : 'Product Card'
        }
      />
    </div>
  );
};

const InternalProductCard = ({
  allowNotifications,
  name = 'PRODUCT_CARD',
  pagePosition,
  product,
  padImage,
  cardSize,
  listId = null,
  className,
  displayType,
  modulePosition,
  moduleType,
  position,
  onClick,
  imageLoading,
  isMetadataEndAligned,
  showNotificationCount = false,
  pageType,
  overrideInteractionPagePosition = false,
  withMetadata
}: InternalProps) => {
  const user = useUser();
  const { isBot } = useRootData();
  const impressionRef = useProductCardImpression({
    displayType,
    isBot,
    modulePosition,
    moduleType,
    position,
    product
  });

  return (
    <div ref={impressionRef} className={`relative ${className}`}>
      <Tracked.InternalLink
        data-cy="product-card"
        title={product.name}
        aria-label={product.name}
        to={product.path}
        onClick={() => {
          if (!isBot) {
            new ProductCardClickEvent({
              isBot,
              isClickOut: false,
              listId,
              pagePosition,
              pageType,
              position,
              product,
              user
            }).send();

            new ProductCardInteractionEvent({
              displayType,
              gesture: 'click',
              guestId: user.guestId,
              isBot,
              modulePosition,
              moduleType,
              position,
              product
            }).send();
          }

          if (onClick) onClick();
        }}
        className="flex cursor-pointer flex-col justify-center gap-3.5 overflow-hidden"
        variant="none"
        name={name}
        pagePosition={pagePosition}
      >
        <CardContents
          allowNotifications={allowNotifications}
          cardSize={cardSize}
          padImage={padImage}
          pagePosition={pagePosition}
          product={product}
          imageLoading={imageLoading}
          isMetadataEndAligned={isMetadataEndAligned}
          withMetadata={withMetadata}
          showNotificationCount={showNotificationCount}
        />
      </Tracked.InternalLink>
      <CardInteraction
        product={product}
        allowNotifications={allowNotifications}
        modulePosition={modulePosition}
        moduleType={moduleType}
        position={position}
        pagePosition={
          overrideInteractionPagePosition ? pagePosition : 'Product Card'
        }
      />
    </div>
  );
};

function CardContents({
  product,
  cardSize,
  padImage,
  imageLoading,
  isMetadataEndAligned,
  showNotificationCount,
  withMetadata = true
}: Omit<Props, 'moduleType'> & {
  product: ProductListItemExternal | ProductListItemInternal;
}) {
  return (
    <>
      <div className="hover:opacity-80">
        <div className="absolute left-2 top-2 lg:left-4 lg:top-4">
          <StockStatus
            status={product.stock_status}
            releaseDate={product.date?.released}
            releaseDateFormat={cardSize === 'large' ? 'long' : 'short'}
          />
        </div>
        <ProductImage
          alt={product.image?.alt}
          url={product.image?.url}
          cropToFill={product.sub_type === 'clothing'}
          padImage={padImage}
          cardSize={cardSize}
          imageLoading={imageLoading}
        />
        {(showNotificationCount === 'always' ||
          (showNotificationCount && !!product.weekly_notifications_count)) && (
          <div className="mt-2 bg-white/90">
            <ProductNotificationCount
              count={product.weekly_notifications_count ?? 0}
            />
          </div>
        )}
      </div>

      {withMetadata && (
        <ImageMetadata
          affiliate={
            product.type === 'external' ? product.affiliate : undefined
          }
          isEndAligned={isMetadataEndAligned}
          linkType="product"
          price={product.price}
          title={product.name}
          variantCount={
            product.sub_type === 'clothing'
              ? product.num_of_variants
              : undefined
          }
        />
      )}
    </>
  );
}

const IMAGE_SIZES = {
  large: { height: 1000 as const, width: 800 as const },
  small: { height: 720 as const, width: 576 as const }
};

const ProductImage = ({
  alt,
  cardSize,
  cropToFill,
  imageLoading,
  padImage,
  url
}: {
  alt?: string;
  cardSize: 'small' | 'large';
  cropToFill: boolean;
  imageLoading: Props['imageLoading'];
  padImage: boolean;
  url?: string;
}) => {
  if (!url) return <ImageSkeleton />;

  const { height, width } = IMAGE_SIZES[cardSize];

  const fit = cropToFill
    ? { className: 'size-full object-cover', pad: false, retainRatio: false }
    : {
        className: 'size-full object-contain',
        pad: padImage,
        retainRatio: !padImage
      };

  return (
    <ImageSkeleton>
      <Image
        alt={alt}
        src={url}
        height={height}
        width={width}
        pad={fit.pad}
        retainRatio={fit.retainRatio}
        quality={100}
        loading={imageLoading}
        className={fit.className}
        responsiveOptions={{
          lg: {
            ...IMAGE_SIZES.large,
            pad: fit.pad,
            retainRatio: fit.retainRatio
          },
          sm: {
            ...IMAGE_SIZES.small,
            pad: fit.pad,
            retainRatio: fit.retainRatio
          }
        }}
      />
    </ImageSkeleton>
  );
};

const ImageSkeleton = ({ children }: { children?: JSX.Element }) => {
  return (
    <div className="flex aspect-[4/5] w-full items-center justify-center bg-white">
      {children}
    </div>
  );
};

const CardInteraction = ({
  product,
  allowNotifications,
  modulePosition,
  moduleType,
  pagePosition,
  position
}: {
  allowNotifications: boolean;
  modulePosition?: number;
  moduleType?: string;
  pagePosition: PAGE_POSITIONS;
  position?: number;
  product: ProductListItem;
}) => {
  const notificationsData = {
    id: product.id,
    image: product.image,
    legacyId: product.legacy_id,
    name: product.name,
    price: product.price,
    queryID: product.queryID,
    stockStatus: product.stock_status
  };

  if (allowNotifications) {
    return (
      <div className="absolute right-2 top-[12px] lg:right-4 lg:top-[20px]">
        <ProductNotificationButton
          product={notificationsData}
          modulePosition={modulePosition}
          moduleType={moduleType}
          pagePosition={pagePosition}
          placement="product_card"
          position={position}
          buttonStyle="compact"
        />
      </div>
    );
  }

  return null;
};

interface InlineExternalProps {
  cardSize: 'small' | 'large';
  className?: string;
  displayType: ProductCardDisplayType;
  imageLoading?: 'lazy' | 'eager';
  isMetadataEndAligned?: boolean;
  modulePosition?: number;
  moduleType: string;
  name?: TrackedButtonClickName;
  padImage: boolean;
  pagePosition: PAGE_POSITIONS;
  position: number;
  product: ProductListItemInlineExternal;
  withMetadata?: boolean;
}

const InlineExternalProductCard = ({
  cardSize,
  className,
  displayType,
  imageLoading,
  isMetadataEndAligned,
  modulePosition,
  moduleType,
  name,
  padImage,
  pagePosition,
  position,
  product,
  withMetadata = true
}: InlineExternalProps) => {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot, session } = useRootData();
  const { pathname, search } = useLocation();

  const { url, clickRef } = getTrackedUrlAndClickRef(product, { user });

  const trackImpression = useCallback(() => {
    new InlineProductCardImpressionEvent({
      displayType,
      guestId: user.guestId,
      isBot,
      modulePosition,
      moduleType,
      position,
      product
    }).track();
  }, [
    displayType,
    isBot,
    modulePosition,
    moduleType,
    position,
    product,
    user.guestId
  ]);

  const impressionRef = useImpression<HTMLDivElement>(trackImpression, {
    enabled: !isBot
  });

  const onClick = () => {
    if (isBot) return;

    new InlineProductCardInteractionEvent({
      clickref: clickRef,
      displayType,
      gesture: 'click',
      guestId: user.guestId,
      isBot,
      modulePosition,
      moduleType,
      position,
      product
    }).send();

    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      session,
      url
    });

    if (product.affiliate)
      new AffiliateLinkClickEvent({
        affiliateName: product.affiliate.name,
        clientIp,
        isBot,
        isReseller: product.affiliate.is_reseller,
        pagePosition,
        product: { name: product.name, price: product.price },
        retailerClickPrice:
          product.price.sale ?? product.price.regular ?? undefined,
        retailerId: product.affiliate.id,
        user
      }).send();
  };

  return (
    <div ref={impressionRef} className={`relative ${className}`}>
      <Tracked.ExternalLink
        href={url}
        target="_blank"
        rel="sponsored nofollow noopener"
        name={name ?? `PRODUCT_CARD_${product.name}`}
        pagePosition={pagePosition}
        affiliate={product.affiliate?.name}
        variant="none"
        className="flex cursor-pointer flex-col items-center justify-center gap-3.5 overflow-hidden"
        onClick={onClick}
      >
        <div className="bg-white">
          <div className="hover:opacity-80">
            <ProductImage
              alt={product.image?.alt}
              cardSize={cardSize}
              cropToFill={false}
              imageLoading={imageLoading}
              padImage={padImage}
              url={product.image?.url}
            />
          </div>
        </div>

        {withMetadata && (
          <ImageMetadata
            affiliate={product.affiliate}
            isEndAligned={isMetadataEndAligned}
            linkType="product"
            price={product.price}
            title={product.name}
          />
        )}
      </Tracked.ExternalLink>
    </div>
  );
};

export default {
  External: ExternalProductCard,
  InlineExternal: InlineExternalProductCard,
  Internal: InternalProductCard
};
